<template>
  <div class="emat-block-dropdown" :class="device">
    <div class="emat-block-heading" @click="handleDropdown">
      <car-svg v-if="icon === 'car'" class="icon car" />
      <cogwheel-svg v-if="icon === 'cogwheel'" class="icon" />
      <span class="name" v-text="name"></span>
      <triangle-svg class="chevron"
                    :class="{'active': dropdown}" />
    </div>
    <transition name="slide-fade">
      <div v-if="dropdown" class="emat-dropdown-body" :class="device">
        <div class="emat-close" @click="dropdown = false"></div>
        <span class="emat-dropdown-title" v-text="dropdownTitle"></span>
        <p class="text" v-text="innerText"></p>

        <div class="emat-dropdown-content">
          <div class="left half">
            <span class="title"><slot name="left-title"></slot></span>
            <slot name="left-content"></slot>
          </div>
          <div class="right half">
            <span class="title"><slot name="right-title"></slot></span>
            <slot name="right-content"></slot>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CarSvg from '../../../assets/icons/car.svg?inline';
import CogwheelSvg from '../../../assets/icons/gears.svg?inline';
import TriangleSvg from '../../../assets/icons/triangle-dark-blue.svg?inline';

import { mapGetters } from 'vuex';

export default {
  name: 'BlockDropdown',
  props: {
    /**
     * Unique name for your input
     */
    name: {
      type: String,
      required: true,
    },
    /**
     * Title inside dropdown block
     */
    dropdownTitle: {
      type: String,
      required: false,
    },
    /**
     * Text under dropdown title
     */
    innerText: {
      type: String,
      required: false,
    },
    /**
     * Icon name = car or cogwheel
     */
    icon: {
      type: String,
      required: true,
    },
    /**
     * Boolean dropdown or not
     */
    drop: {
      type: Boolean,
      required: true,
      default: false,
    },
    /**
     * Disabled
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    CarSvg,
    CogwheelSvg,
    TriangleSvg,
  },
  data() {
    return {
      dropdown: false,
    };
  },

  methods: {
    handleDropdown() {
      if (this.drop) {
        this.dropdown = !this.dropdown;
      } else {
        this.$emit('previous');
      }
    },
    clickOutside(e) {
      let self = this;
      // close dropdown when clicked outside
      if (!self.$el.contains(e.target)) {
        self.dropdown = false;
      }
    },
  },

  computed: {
    ...mapGetters({
      device: 'getDevice',
    }),
  },
  created() {
    window.addEventListener('click', this.clickOutside);
  },
  beforeDestroy() {
    window.removeEventListener('click', this.clickOutside);
  },
};
</script>

<style lang="scss">
@import "../../../assets/sass/abstracts";
.emat-block-dropdown {
  cursor: pointer;
  user-select: none;
  background-color: #fff;
  min-width: 141px;
  height: 60px;
  position: relative;

  &:last-child {
    border-bottom-right-radius: $border-radius;
    border-top-right-radius: $border-radius;
  }

  &.mobile {
    min-width: 60px;

    > .emat-block-heading {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
    }
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.2;
  }

  .emat-block-heading {
    @include header-font;
    color: var(--c-primary);
    font-size: 10px;
    display: flex;
    text-align: center;
    align-items: center;
    flex-direction: column;
    position: relative;

    .name {
      position: relative;
      max-width: 116px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      top: 32px;
    }

    .icon {
      position: absolute;
      top: 0;

      &.car {
        margin-top: -6px;
      }
    }

    .chevron {
      width: 10px;
      height: 12px;
      transition: all ease 0.3s;
      margin-top: 34px;

      &.active {
        transform: rotate(180deg);
      }
    }
  }

  .emat-dropdown-body {
    cursor: default;
    z-index: 10;
    padding: 0 20px;
    box-shadow: 0 0 10px rgba(0, 0, 0, .2);
    top: 100%;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: white;
    border-radius: $border-radius;
    position: absolute;
    min-width: 420px;
    height: 348px;

    &.mobile {
      top: 0;
      position: relative;
      height: auto;
      max-height: calc(100vh - 250px);
      overflow-y: auto;
    }

    @media only screen and (max-width: 576px) {
      min-width: 250px;
      width: 100%;

      .emat-dropdown-content {
        flex-direction: column;
      }
    }

    &.slide-fade-enter-active {
      transform: translateY(0);
      transition: all ease 0.3s;
      opacity: 1;
    }

    &.slide-fade-leave-active {
      transform: translateY(-50%);
      transition: all ease 0.3s;
      opacity: 0;
    }

    &.slide-fade-enter, .slide-fade-leave-to {
      transform: translateY(-50%);
      opacity: 0;
    }

    .emat-dropdown-title {
      @include header-font;
      padding: 10px 0;
      color: var(--c-title);
      max-width: 320px;
      font-size: 18px;
      display: inline-block;
    }

    .emat-close {
      width: 20px;
      height: 20px;
      position: absolute;
      right: 10px;
      top: 10px;
      cursor: pointer;
      z-index: 5;

      &:before,
      &:after {
        content: "";
        display: block;
        height: 2px;
        width: 18px;
        background: var(--c-popup-background);
        position: absolute;
        top: 50%;
        left: 50%;
      }

      &:before {
        transform: translateX(-50%) translateY(-50%) rotate(45deg);
      }

      &:after {
        transform: translateX(-50%) translateY(-50%) rotate(-45deg);
      }
    }

    .text {
      margin-top: 12px;
      font-size: 14px;
      font-family: var(--font);
      color: $black;
      max-width: 380px;
    }

    .emat-dropdown-content {
      display: flex;
      background-color: var(--c-primary-lightest);
      margin-left: -20px;
      margin-right: -20px;
      padding: 18px 20px 30px;
      border-bottom-left-radius: $border-radius;
      border-bottom-right-radius: $border-radius;
      box-shadow: $box-shadow;

      .emat-table {
        margin-top: 10px;
        font-family: var(--font);
        font-size: 12px;
        border-collapse: collapse;
        border-radius: $border-radius;
        border-style: hidden;
        box-shadow: 0 0 0 1px $gray;
        overflow: hidden;

        th, td {
          padding: 5px 15px;
        }

        thead {
          font-weight: $font-weight-bold;
          margin-left: 32px;
          margin-bottom: 4px;
          border-bottom: 1px solid $gray;

          th {
            padding: 5px 0;
            text-align: center;
          }
        }

        tbody {
          tr {
            background-color: $white;
          }

          tr:nth-child(odd) {
            background-color: var(--c-zebra-stripe);
          }

          td {
            border-right: 1px solid $gray;
            width: 50%;

            img {
              margin-right: 10px;
            }

            &:nth-child(2) {
              text-align: center;
            }
          }
        }
      }

      .title {
        @include header-font;
        color: var(--c-primary);
        font-size: 18px;
      }

      .half {
        min-width: 50%;
        height: auto;
      }

      .right {
        padding-left: 20px;
        display: flex;
        flex-direction: column;

        @media only screen and (max-width: 820px) {
          padding-left: 0;
        }

        .title {
          @include header-font;
          color: var(--c-title);
          font-size: 18px;
        }

        .icon {
          margin-top: 25px;
          align-self: center;
        }
      }

      .left {
        position: relative;

      }
    }
  }
}
</style>
